import { useState } from 'react'
import { Minus, Plus, ShoppingBag } from 'lucide-react'
import type { InventoryItemNameRow } from '@/lib/api'
import { useInventoryCart } from '@/hooks/useInventoryCart'
import {
  effectiveUnitPrice,
  hasClearancePricing,
  itemAllowsCart,
  itemMetaLine,
  maxStockCap,
} from '@/lib/inventoryItemDisplay'
import { SuperscriptPrice } from '@/components/store/InventoryItemTile'
import Sheet from '@/components/ui/Sheet'

type Props = {
  item: InventoryItemNameRow
  onClose: () => void
}

/** Item details + quantity picker, shown in a `Sheet`. */
export default function ItemDetailSheet({ item, onClose }: Props) {
  const { getQty, setQty } = useInventoryCart()
  const inCart = getQty(item.inventory_id)
  const allowsCart = itemAllowsCart(item.in_stock)
  const maxStock = maxStockCap(item.in_stock)
  const onSale = hasClearancePricing(item)
  const unitPrice = effectiveUnitPrice(item)
  const [qty, setLocalQty] = useState(inCart > 0 ? inCart : 1)
  const minQty = inCart > 0 ? 0 : 1
  const atMax = maxStock !== undefined && qty >= maxStock
  const unchanged = inCart > 0 && qty === inCart

  const confirm = () => {
    setQty(item.inventory_id, qty, item.in_stock)
    onClose()
  }

  let actionLabel: string
  if (inCart === 0) actionLabel = 'Add to cart'
  else if (qty === 0) actionLabel = 'Remove from cart'
  else if (unchanged) actionLabel = 'In cart'
  else actionLabel = 'Update cart'

  const meta = itemMetaLine(item)
  const name = item.item_name || '(No name)'
  const desc = item.description?.trim()
  // POS rows often carry "0" or a copy of the name as description.
  const hasUsefulDescription = !!desc && /[a-z]/i.test(desc) && desc !== item.item_name

  return (
    <Sheet
      onClose={onClose}
      labelledBy="item-detail-title"
      footer={
        <>
          {allowsCart ? (
            <>
              <div
                className="flex h-12 items-center rounded-full border border-stone-300"
                role="group"
                aria-label="Quantity"
              >
                <button
                  type="button"
                  onClick={() => setLocalQty((q) => Math.max(minQty, q - 1))}
                  disabled={qty <= minQty}
                  className="flex h-12 w-12 items-center justify-center rounded-full text-stone-800 disabled:opacity-30"
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-4 w-4" strokeWidth={2.5} />
                </button>
                <span className="w-8 text-center text-base font-semibold tabular-nums text-stone-900" aria-live="polite">
                  {qty}
                </span>
                <button
                  type="button"
                  onClick={() => setLocalQty((q) => (atMax ? q : q + 1))}
                  disabled={atMax}
                  className="flex h-12 w-12 items-center justify-center rounded-full text-stone-800 disabled:opacity-30"
                  aria-label="Increase quantity"
                  title={atMax ? 'Maximum available' : undefined}
                >
                  <Plus className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </div>
              <button
                type="button"
                onClick={confirm}
                disabled={unchanged}
                className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold text-white shadow-sm transition-colors disabled:opacity-50 ${
                  qty === 0 && inCart > 0
                    ? 'bg-stone-800 hover:bg-stone-900'
                    : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800'
                }`}
              >
                <span>{actionLabel}</span>
                {qty > 0 && unitPrice != null && !unchanged && (
                  <span className="tabular-nums opacity-90">· ${(unitPrice * qty).toFixed(2)}</span>
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled
              className="h-12 flex-1 rounded-full bg-stone-200 text-sm font-semibold text-stone-500"
            >
              Currently unavailable
            </button>
          )}
        </>
      }
    >
      <div className="relative mx-4 mt-3 flex aspect-[16/10] items-center justify-center rounded-2xl bg-stone-100 sm:mt-4">
        <ShoppingBag className="h-16 w-16 text-stone-300" strokeWidth={1.25} aria-hidden />
        {onSale && (
          <span className="absolute left-3 top-3 rounded-md bg-primary-600 px-2 py-0.5 text-xs font-bold text-white">
            {item.discount_percentage}% OFF
          </span>
        )}
      </div>

      <div className="space-y-3 px-5 pb-4 pt-4">
        {meta && (
          <p className="text-xs font-medium uppercase tracking-wide text-stone-500">{meta}</p>
        )}
        <h2 id="item-detail-title" className="text-lg font-semibold leading-snug text-stone-900">
          {name}
        </h2>

        <div className="flex items-baseline gap-2">
          {unitPrice != null ? (
            <SuperscriptPrice value={unitPrice} size="lg" tone={onSale ? 'sale' : 'default'} />
          ) : (
            <span className="text-base text-stone-400">Price N/A</span>
          )}
          {onSale && (
            <span className="text-sm text-stone-400 line-through tabular-nums">
              ${item.retail_price!.toFixed(2)}
            </span>
          )}
        </div>

        <p
          className={`text-sm font-medium ${
            allowsCart ? 'text-emerald-700' : 'text-red-600'
          }`}
        >
          {!allowsCart
            ? 'Out of stock'
            : maxStock !== undefined && maxStock <= 5
              ? `Only ${maxStock} left`
              : 'In stock'}
        </p>

        {hasUsefulDescription && (
          <p className="text-sm leading-relaxed text-stone-600">{item.description}</p>
        )}

        <p className="font-mono text-xs text-stone-400">Item #{item.item_num}</p>
      </div>
    </Sheet>
  )
}
